import {Component, OnInit} from '@angular/core';

import {HgPartnerDto} from "../../../../model/dto/entity/hg-partner.dto";
import {ActivatedRoute, Router} from "@angular/router";
import {HgPartnerService} from "../../../../service/api/hg-partner.service";
import {ObjectBaseModeComponent} from "../../../../../../core/shared/object/mvs-object-base/object-base-mode.component";
import {WidgetData} from "../../../../../../core/shared/widget/widget-data";
import {WidgetFactory} from "../../../../../../core/shared/widget/service/widget.factory";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";
import {MvsMessageService} from "../../../../../../core/shared/service/message/mvs-message.service";
import {HgImportDto} from "../../../../model/dto/entity/hg-import.dto";
import {EiImportDto} from "../../../../../ei/model/dto/entity/ei-import.dto";
import {EiImportPhase} from "../../../../../ei/enum/ei-import-phase.enum";
import {EiImportService} from "../../../../../ei/service/api/ei-import.service";

@Component({
    selector: 'hg-gdv-partner-base',
    template: '',
    standalone: false
})
export class HgGdvPartnerBaseComponent extends ObjectBaseModeComponent implements OnInit {

    basicWidgetData: WidgetData;
    hgImportsWidgetData: WidgetData;
    dto: HgPartnerDto;

    busy: boolean;

    /**
     * Exposed to the template so the "Zur Objekt-Prüfung" button can use the
     * phase enum for comparison.
     */
    protected readonly EiImportPhase = EiImportPhase;

    constructor(
        protected route: ActivatedRoute,
        protected hgPartnerService: HgPartnerService,
        protected messageService: MvsMessageService,
        protected router: Router,
        protected eiImportService: EiImportService) {
        super(route);
    }

    ngOnInit(): void {

        if (this.navigationItems?.length) {
            this.activeNavigationItem = this.navigationItems.find(item => item.default == true);
            this.onNavigationItems.emit(this.navigationItems);
        }

        super.ngOnInit();
        this.refreshWidgets();
    }

    refreshWidgets() {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Entity data widget displaying basic details of the current HgPartner
         */
        this.basicWidgetData = WidgetFactory.createWidgetEntityData(
            'hg.gdv.partner.basic.widget.data',
            'GDV Partner',
            'hg.HgPartner',
            this.objectIdentifier.objectId
        );

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Lists HgImport entries filtered by the current HgPartner
         */
        this.hgImportsWidgetData = WidgetFactory.createWidgetListEntityBasicQl(
            'hg.partner.imports.list.basic',
            'GDV-Importe',
            'hg.HgImport',
            [FilterCriteria.create('e.hgPartnerId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)]
        );
    }

    /**
     * Handles a row selection in the HgImports widget.
     *
     * If the selected HgImport has an associated EiImport whose phase is
     * OBJECT_REVIEW, navigates to the generic multi-stage object-review page.
     * Otherwise does nothing (the user remains on the partner page).
     *
     * Note: This is the deep-link entry point into the Multi-Stage Import
     * Object-Review workflow. Once a dedicated HgImport detail page exists,
     * the button should move there.
     */
    handleHgImportSelect(event: any): void {
        const data: HgImportDto = event?.data ?? event;
        if (!data || !data['eiImportDtoId']) {
            return;
        }

        const eiImportId = data['eiImportDtoId'] as number;
        this.eiImportService.get(eiImportId).subscribe({
            next: (dto) => {
                const eiImport = dto as EiImportDto;
                if (eiImport?.phase === EiImportPhase.OBJECT_REVIEW) {
                    this.router.navigate(['ei/import', eiImportId, 'object-review']);
                } else {
                    this.messageService.showInfoMessage(
                        'Objekt-Prüfung nicht verfügbar',
                        'Dieser Import ist nicht in der Phase Objekt-Prüfung.',
                    );
                }
            },
            error: (err) => {
                console.error('Fehler beim Laden des EiImport', err);
                this.messageService.showErrorMessage(
                    'Ladefehler',
                    'Der zugehörige EI-Import konnte nicht geladen werden.',
                );
            },
        });
    }

    /**
     * Uploads a GDV file for the current partner.
     * Triggered by the p-fileUpload component's uploadHandler event.
     */
    uploadGdvFile(event: any): void {
        const file = event.files ? event.files[0] : event.target?.files?.[0];
        if (!file) return;

        this.busy = true;

        this.hgPartnerService.uploadGdvFile(this.objectIdentifier.objectId, file).subscribe({
            next: () => {
                this.busy = false;
                this.messageService.showSuccessMessage('Upload erfolgreich', 'GDV-Datei erfolgreich hochgeladen.');
                // Refresh imports widget to show the new entry
                this.refreshWidgets();
            },
            error: (err) => {
                this.busy = false;
                this.messageService.showErrorMessage('Upload fehlgeschlagen', err.error?.message || 'Unbekannter Fehler');
            }
        });
    }

}
